import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { theme } from '@factorialco/deprecated-design-system'
import { useF0Dialog } from '@factorialco/f0-react'
import { createPortal } from 'react-dom'

import type { AnchorRect } from 'modules/job_catalog/benchmark/types'

interface Props {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  ariaLabel: string
  anchorElement?: HTMLElement | null
  anchorHorizontalInset?: number
}

/**
 * Low-level Popover component providing full layout control without design system constraints.
 * Uses a portal to render content without imposing a visual shell or requiring a trigger element.
 * Handles Escape key and focuses the dialog container on open for keyboard accessibility.
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false)
 * return (
 *   <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} ariaLabel="Details">
 *     <YourCustomContent />
 *   </Popover>
 * )
 */
const Popover = ({
  isOpen,
  onClose,
  children,
  ariaLabel,
  anchorElement,
  anchorHorizontalInset = 0,
}: Props) => {
  const { portalContainer } = useF0Dialog()
  const overlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const anchorRectFrameRef = useRef<number | null>(null)
  const [contentRect, setContentRect] = useState<{ width: number; height: number } | null>(null)
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null)

  useLayoutEffect(() => {
    if (!isOpen) return

    const content = contentRef.current
    if (!content) return

    const measure = () => {
      const rect = content.getBoundingClientRect()
      setContentRect({ width: rect.width, height: rect.height })
    }

    measure()
    window.addEventListener('resize', measure)

    return () => {
      window.removeEventListener('resize', measure)
    }
  }, [isOpen, children])

  useLayoutEffect(() => {
    if (!isOpen || !anchorElement) {
      setAnchorRect(null)
      return
    }

    const updateAnchorRect = () => {
      const rect = anchorElement.getBoundingClientRect()
      setAnchorRect({
        top: rect.top,
        left: rect.left + anchorHorizontalInset,
        width: Math.max(0, rect.width - anchorHorizontalInset * 2),
        height: rect.height,
      })
    }

    const scheduleAnchorRectUpdate = () => {
      if (anchorRectFrameRef.current != null) return

      anchorRectFrameRef.current = window.requestAnimationFrame(() => {
        anchorRectFrameRef.current = null
        updateAnchorRect()
      })
    }

    updateAnchorRect()
    window.addEventListener('resize', scheduleAnchorRectUpdate)
    window.addEventListener('scroll', scheduleAnchorRectUpdate, true)

    return () => {
      if (anchorRectFrameRef.current != null) {
        window.cancelAnimationFrame(anchorRectFrameRef.current)
        anchorRectFrameRef.current = null
      }

      window.removeEventListener('resize', scheduleAnchorRectUpdate)
      window.removeEventListener('scroll', scheduleAnchorRectUpdate, true)
    }
  }, [anchorElement, anchorHorizontalInset, isOpen])

  useEffect(() => {
    if (isOpen) {
      overlayRef.current?.focus()
    }
  }, [isOpen])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    },
    [onClose]
  )

  if (!isOpen) return null

  const horizontalOffset = 8
  const verticalOffset = 8
  const viewportPadding = 32
  const contentWidth = contentRect?.width ?? 392
  const contentHeight = contentRect?.height ?? 156
  const canPlaceBelow =
    anchorRect != null &&
    anchorRect.top + anchorRect.height + verticalOffset + contentHeight <=
      window.innerHeight - viewportPadding

  const top = anchorRect
    ? canPlaceBelow
      ? anchorRect.top + anchorRect.height + verticalOffset
      : Math.max(viewportPadding, anchorRect.top - contentHeight - verticalOffset)
    : window.innerHeight / 2 - contentHeight / 2

  const left = (() => {
    if (!anchorRect) return window.innerWidth / 2 - contentWidth / 2

    const leftAlignedLeft = anchorRect.left + anchorRect.width - contentWidth - horizontalOffset
    const centerAlignedLeft = anchorRect.left + anchorRect.width / 2 - contentWidth / 2
    const rightAlignedLeft = anchorRect.left + anchorRect.width + horizontalOffset
    const maxLeft = window.innerWidth - contentWidth - viewportPadding
    const leftFits =
      leftAlignedLeft >= viewportPadding &&
      leftAlignedLeft + contentWidth <= window.innerWidth - viewportPadding
    const centerFits =
      centerAlignedLeft >= viewportPadding &&
      centerAlignedLeft + contentWidth <= window.innerWidth - viewportPadding
    const rightFits = rightAlignedLeft + contentWidth <= window.innerWidth - viewportPadding

    if (leftFits) return leftAlignedLeft
    if (centerFits) return centerAlignedLeft
    if (rightFits) return rightAlignedLeft

    const clampedLeftLeft = Math.max(viewportPadding, Math.min(leftAlignedLeft, maxLeft))
    const clampedCenterLeft = Math.max(viewportPadding, Math.min(centerAlignedLeft, maxLeft))
    const clampedRightLeft = Math.max(viewportPadding, Math.min(rightAlignedLeft, maxLeft))

    if (clampedLeftLeft === leftAlignedLeft) return clampedLeftLeft
    if (clampedCenterLeft === centerAlignedLeft) return clampedCenterLeft
    if (clampedRightLeft === rightAlignedLeft) return clampedRightLeft

    return clampedCenterLeft
  })()

  return createPortal(
    // eslint-disable-next-line factorial/no-raw-html -- Portal overlay requires fixed positioning + inset + zIndex unsupported by F0Box
    <div
      ref={overlayRef}
      data-benchmark-popover-portal
      role="dialog"
      aria-modal
      aria-label={ariaLabel}
      tabIndex={-1}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modals,
        pointerEvents: 'auto',
        outline: 'none',
        background: 'hsl(var(--neutral-20) / 0.3)',
      }}
      onClick={() => {
        onClose()
      }}
      onKeyDown={handleKeyDown}
    >
      {/* eslint-disable-next-line factorial/no-raw-html -- Absolute positioning for anchored popover placement unsupported by F0Box */}
      <div
        ref={contentRef}
        style={{
          position: 'absolute',
          top,
          left,
          pointerEvents: 'auto',
        }}
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {children}
      </div>
    </div>,
    portalContainer ?? document.body
  )
}

export default Popover
